/**
 * @file routes/_app/me/profile.tsx
 * @description 個人資料設置頁面路由（需認證）。
 *
 * 功能：
 * - 顯示當前用戶的個人資料（姓名、信箱、電話）
 * - 頭像展示（首字母）
 * - 提交更新後調用 PATCH /api/me/profile
 * - 成功後同步更新 AuthStore 中的用戶信息
 * - 重置密碼：輸入舊密碼 + 新密碼，後端 PATCH /api/me/password 校驗舊密碼
 *
 * 視覺：PageShell + SectionCard + FormSection
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Avatar, Button, Form, Input } from 'antd';
import { message } from '@/lib/antd-app';
import { Camera, KeyRound, Lock, Mail, Smartphone, User } from 'lucide-react';
import { PASSWORD_RULE_HINT, USER_ROLE_LABELS } from '@zhcb/shared';
import { userPasswordRule } from '@/lib/password-rules';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, type AuthUser } from '@/lib/auth-context';
import { PageShell, SectionCard, FormSection, StatusPill } from '@/components/shell';
import { color, font, space, text, weight } from '@/styles/tokens';

/** 路由定義 */
export const Route = createFileRoute('/_app/me/profile')({ component: ProfilePage });

/** 角色標籤映射（沿用 @zhcb/shared 的全系統統一文案） */
const ROLE_LABEL = USER_ROLE_LABELS as Record<string, string>;

/**
 * 個人資料頁面。
 * 初始值從 AuthStore 加載，提交後 PATCH /api/me/profile 並更新 store。
 * 下方另有「重置密碼」卡片，走獨立表單與獨立端點。
 */
function ProfilePage() {
  const user = useAuthStore((s) => s.user)!;
  const setUser = useAuthStore((s) => s.setUser);

  /**
   * 提交個人資料更新。
   * 成功後更新本地 AuthStore，使 Header 顯示名稱即時更新。
   */
  const onFinish = async (v: { name: string; email: string; phone?: string }) => {
    try {
      await apiClient.patch('/api/me/profile', v);
      setUser({ ...user, ...v });
      message.success('個人資料已更新');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '更新失敗');
    }
  };

  return (
    <PageShell
      title="個人資料"
      description="管理您的基本資訊與聯絡方式"
      meta={
        <StatusPill tone="brand" size="sm">
          {ROLE_LABEL[user.primaryRole] ?? user.primaryRole}
        </StatusPill>
      }
    >
      <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: space[5] }}>
        <ProfileCard user={user} onFinish={onFinish} />
        <PasswordCard />
      </div>
    </PageShell>
  );
}

/**
 * 基本資訊卡片：頭像 + 姓名/信箱/電話表單。
 */
function ProfileCard({
  user,
  onFinish,
}: {
  user: AuthUser;
  onFinish: (v: { name: string; email: string; phone?: string }) => Promise<void>;
}) {
  return (
    <SectionCard>
      {/* 頭像區 */}
      <FormSection title="頭像" description="您的頭像顯示在系統各處的個人識別" bordered>
        <div style={{ display: 'flex', alignItems: 'center', gap: space[5] }}>
          <Avatar
            size={84}
            style={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              fontSize: text['2xl'],
              fontFamily: font.display,
              fontWeight: weight.bold,
              flexShrink: 0,
            }}
          >
            {user.name?.charAt(0) ?? '?'}
          </Avatar>
          <div>
            <Button icon={<Camera size={14} />} disabled>
              更換頭像
            </Button>
            <div style={{ marginTop: space[2], fontSize: text.sm, color: color.text.tertiary }}>
              建議使用正方形圖片，最佳尺寸 256 × 256（暫不支援上傳）
            </div>
          </div>
        </div>
      </FormSection>

      {/* 基本資訊表單 */}
      <FormSection title="基本資訊" description="顯示在系統各處的個人識別">
        <Form
          initialValues={{ name: user.name, email: user.email }}
          onFinish={onFinish}
          layout="vertical"
          style={{ maxWidth: 560 }}
        >
          {/* 唯讀資訊行 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: space[4],
            }}
          >
            <Form.Item label="用戶名">
              <Input value={user.username} disabled />
            </Form.Item>
            <Form.Item label="角色">
              {/*
               * 個人頁展示主角色（primaryRole）文字即可；
               * 回落邏輯（?? user.primaryRole）處理自定義崗位等未知代碼。
               * 能力判斷由後端 userCan 負責，此處只做純顯示，局限於此為刻意設計。
               */}
              <Input value={ROLE_LABEL[user.primaryRole] ?? user.primaryRole} disabled />
            </Form.Item>
          </div>

          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '請輸入姓名' }]}>
            <Input prefix={<User size={14} />} placeholder="請輸入姓名" />
          </Form.Item>

          <Form.Item
            name="email"
            label="電子信箱"
            rules={[
              { required: true, message: '請輸入電子信箱' },
              { type: 'email', message: '請輸入有效的電子信箱格式' },
            ]}
          >
            <Input prefix={<Mail size={14} />} placeholder="請輸入電子信箱" />
          </Form.Item>

          <Form.Item name="phone" label="手機號碼">
            <Input prefix={<Smartphone size={14} />} placeholder="選填" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">
              儲存變更
            </Button>
          </Form.Item>
        </Form>
      </FormSection>
    </SectionCard>
  );
}

/** 重置密碼表單的字段型別 */
interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * 重置密碼卡片。
 * 輸入舊密碼 + 新密碼（兩次），提交 PATCH /api/me/password；
 * 舊密碼由後端 verifyPassword 校驗，錯誤回 INVALID_OLD_PASSWORD。
 */
function PasswordCard() {
  const [form] = Form.useForm<PasswordFormValues>();
  const [submitting, setSubmitting] = useState(false);

  /**
   * 提交密碼修改。
   * 前端僅做「兩次新密碼一致」與長度校驗，舊密碼正確性交由後端判定。
   */
  const onFinish = async (v: PasswordFormValues) => {
    setSubmitting(true);
    try {
      await apiClient.patch('/api/me/password', {
        oldPassword: v.oldPassword,
        newPassword: v.newPassword,
      });
      message.success('密碼已更新，請牢記新密碼');
      form.resetFields();
    } catch (e) {
      // 後端舊密碼錯誤回 INVALID_OLD_PASSWORD，message 已是中文文案，直接展示
      message.error(e instanceof Error ? e.message : '修改密碼失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SectionCard>
      <FormSection title="重置密碼" description="修改登入密碼，需先驗證目前的舊密碼">
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ maxWidth: 560 }}>
          <Form.Item
            name="oldPassword"
            label="舊密碼"
            rules={[{ required: true, message: '請輸入舊密碼' }]}
          >
            <Input.Password
              prefix={<Lock size={14} />}
              placeholder="請輸入目前的密碼"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密碼"
            // 序50：6–10 字元、英數混合、不限大小寫（規則與後端共用 @zhcb/shared）
            extra={PASSWORD_RULE_HINT}
            rules={[{ required: true, message: '請輸入新密碼' }, userPasswordRule]}
          >
            <Input.Password
              prefix={<KeyRound size={14} />}
              placeholder={PASSWORD_RULE_HINT}
              maxLength={10}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="確認新密碼"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '請再次輸入新密碼' },
              // 校驗兩次輸入一致：取表單當前 newPassword 值比對
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('兩次輸入的新密碼不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<KeyRound size={14} />}
              placeholder="再次輸入新密碼"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              更新密碼
            </Button>
          </Form.Item>
        </Form>
      </FormSection>
    </SectionCard>
  );
}
